import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Funnel, Plus, RefreshCw } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, PageHeader, ui } from "../../../components/ui";
import { CustomerAllocationTable } from "../components/CustomerAllocationTable";
import {
  EMPTY_CUSTOMER_ALLOCATION_FILTERS,
  CustomerAllocationFiltersModal,
} from "../components/CustomerAllocationFiltersModal";
import { collectPageRecords } from "../../../lib/pagination";
import { inventoryManagementService } from "../../../services/inventoryManagementService";

const PAGE_SIZE = 10;
const FILTER_LOOKUP_PAGE_SIZE = 50;
const CLIENT_FILTER_FETCH_SIZE = 100;

function toCustomerOptions(rows) {
  return (rows || []).map((row) => ({
    value: String(row.id),
    label: row.name || String(row.id),
  }));
}

function mergeFilterOptions(current, incoming) {
  const seen = new Set(current.map((option) => option.value));
  const merged = [...current];
  incoming.forEach((option) => {
    if (seen.has(option.value)) return;
    seen.add(option.value);
    merged.push(option);
  });
  return merged;
}

function countActiveFilters(filters) {
  let count = 0;
  if (filters.customerId && filters.customerId !== "All") count += 1;
  if (filters.allocationType && filters.allocationType !== "All") count += 1;
  return count;
}

function matchesAllocationType(row, allocationType) {
  if (!allocationType || allocationType === "All") return true;
  const type = String(row.allocationType || row.orderType || "")
    .trim()
    .toLowerCase();
  return type === String(allocationType).trim().toLowerCase();
}

async function fetchAllocationsForClientFilter(customerId) {
  return collectPageRecords(
    (page, size) =>
      inventoryManagementService.getCustomerAllocations({
        page,
        size,
        customerId,
      }),
    CLIENT_FILTER_FETCH_SIZE
  );
}

export function CustomerAllocationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    ...EMPTY_CUSTOMER_ALLOCATION_FILTERS,
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false);
  const customerFetchLock = useRef(false);
  const customerLookupRef = useRef({ page: 0, hasMore: false, loaded: false });

  const activeFilterCount = useMemo(
    () => countActiveFilters(filters),
    [filters]
  );

  const needsClientAllocationTypeFilter =
    Boolean(filters.allocationType) && filters.allocationType !== "All";

  const handleCreate = () => {
    navigate("/inventory-management/allocate-to-customer");
  };

  const loadCustomerOptions = useCallback(
    async (pageNum, { append = false } = {}) => {
      if (customerFetchLock.current) return;
      customerFetchLock.current = true;
      if (append) setLoadingMoreCustomers(true);
      else setLoadingCustomers(true);

      try {
        const result =
          await inventoryManagementService.getAllocationCustomers({
            page: pageNum,
            size: FILTER_LOOKUP_PAGE_SIZE,
          });
        const next = toCustomerOptions(result.content);
        setCustomerOptions((prev) =>
          append ? mergeFilterOptions(prev, next) : next
        );
        customerLookupRef.current = {
          page: result.page ?? pageNum,
          hasMore: Boolean(result.hasMore),
          loaded: true,
        };
      } catch (err) {
        console.error("Failed to load customer filter lookups:", err);
        if (!append) {
          setCustomerOptions([]);
          customerLookupRef.current = {
            page: 0,
            hasMore: false,
            loaded: false,
          };
        }
      } finally {
        setLoadingCustomers(false);
        setLoadingMoreCustomers(false);
        customerFetchLock.current = false;
      }
    },
    []
  );

  const handleFilterModalOpenChange = useCallback(
    (open) => {
      setFilterModalOpen(open);
      if (!open) return;
      // Customer list only when Filter is opened — not on page load
      if (!customerLookupRef.current.loaded && !customerFetchLock.current) {
        loadCustomerOptions(0);
      }
    },
    [loadCustomerOptions]
  );

  const handleCustomerListScroll = useCallback(
    (event) => {
      if (!customerLookupRef.current.hasMore || customerFetchLock.current) {
        return;
      }
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
        loadCustomerOptions(customerLookupRef.current.page + 1, {
          append: true,
        });
      }
    },
    [loadCustomerOptions]
  );

  const loadRows = useCallback(() => {
    let active = true;
    setLoading(true);
    setError("");

    const fetchPromise = needsClientAllocationTypeFilter
      ? fetchAllocationsForClientFilter(filters.customerId).then((allRows) => {
          const filtered = allRows.filter((row) =>
            matchesAllocationType(row, filters.allocationType)
          );
          const start = (page - 1) * PAGE_SIZE;
          return {
            content: filtered.slice(start, start + PAGE_SIZE),
            totalElements: filtered.length,
          };
        })
      : inventoryManagementService.getCustomerAllocations({
          page: page - 1,
          size: PAGE_SIZE,
          customerId: filters.customerId,
        });

    fetchPromise
      .then((res) => {
        if (!active) return;
        setRows(res.content || []);
        setTotalItems(res.totalElements ?? (res.content || []).length);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load customer allocations:", err);
        setRows([]);
        setTotalItems(0);
        setError(t("validation.failedToRetrieveCustomerAllocations"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [page, filters, needsClientAllocationTypeFilter, t]);

  useEffect(() => {
    const cleanup = loadRows();
    return cleanup;
  }, [loadRows]);

  const handleApplyFilters = (nextFilters) => {
    setPage(1);
    setFilters({
      customerId: nextFilters.customerId || "All",
      allocationType: nextFilters.allocationType || "All",
    });
  };

  const handleResetFilters = () => {
    setPage(1);
    setFilters({ ...EMPTY_CUSTOMER_ALLOCATION_FILTERS });
  };

  return (
    <AppLayout hideBreadcrumb className="space-y-6">
      <PageHeader
        back={
          <PageBackLink
            to="/inventory-management/stock-position"
            label={t("formActions.back")}
          />
        }
        title={t("inventory.customerAllocation")}
        action={
          <button
            type="button"
            onClick={handleCreate}
            className={ui.primaryBtn}
          >
            <Plus className="h-4 w-4" />
            <span>{t("formActions.create")}</span>
          </button>
        }
      />
      <ContentCard className="overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-(--color-text-border)/70 flex flex-wrap items-center justify-between gap-3 bg-white font-sans">
          <span className="text-xs font-bold text-foreground">
            {t("inventory.customerAllocationsOverview")}
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleFilterModalOpenChange(true)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-workspace/40 hover:text-primary transition cursor-pointer"
            >
              <Funnel className="h-3.5 w-3.5" />
              <span>{t("formActions.filter")}</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-extrabold text-white font-mono leading-none">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={handleResetFilters}
              disabled={activeFilterCount === 0}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-(--color-text-border) bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
            >
              <RefreshCw className="h-3.5 w-3.5 per-file-hint" />
              <span>{t("formActions.resetFilters")}</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-4 animate-pulse">
            <div className="h-10 bg-gray-100 rounded-lg w-full" />
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((idx) => (
                <div
                  key={idx}
                  className="h-12 bg-gray-50/50 rounded-lg w-full flex items-center gap-4 px-4"
                >
                  <div className="h-4 bg-gray-100 rounded w-20" />
                  <div className="h-4 bg-gray-100 rounded w-24" />
                  <div className="h-4 bg-gray-100 rounded w-1/4" />
                  <div className="h-4 bg-gray-100 rounded w-12" />
                  <div className="h-4 bg-gray-100 rounded w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-error font-semibold text-sm">
            ⚠️ {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center font-sans">
            <div className="h-12 w-12 rounded-full bg-workspace per-file-hint grid place-items-center mb-4">
              <span>📦</span>
            </div>
            <h4 className="text-sm font-bold text-foreground">
              {t("inventory.noCustomerAllocationsFound")}
            </h4>
            <p className="text-xs per-file-hint mt-1.5 leading-relaxed max-w-sm">
              {t("inventory.noCustomerAllocationsHint")}
            </p>
          </div>
        ) : (
          <CustomerAllocationTable
            data={rows}
            page={page}
            pageSize={PAGE_SIZE}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        )}
      </ContentCard>

      <CustomerAllocationFiltersModal
        open={filterModalOpen}
        onOpenChange={handleFilterModalOpenChange}
        appliedFilters={filters}
        onApply={handleApplyFilters}
        customerOptions={customerOptions}
        loadingCustomers={loadingCustomers}
        loadingMoreCustomers={loadingMoreCustomers}
        onCustomerListScroll={handleCustomerListScroll}
      />
    </AppLayout>
  );
}
